import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import IdsElement from '../../core/ids-element';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import styles from './ids-form-layout.scss';

import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import {
  FORM_LAYOUT_ATTRIBUTES,
  GAP_TYPES,
  IdsFormLayoutGapType,
  IdsFormLayoutLabelPosition,
  IdsFormLayoutLabelWrap,
  IdsFormLayoutLabelBreak,
  IdsFormLayoutLabelAlignY,
  IdsFormLayoutLabelAlignment,
  IdsFormLayoutFieldHeight,
  IdsFormLayoutFieldSpacing,
  IdsFormLayoutAlignItems,
  IdsFormLayoutSize,
  PropagatedKeys,
  syncSettingToChild,
  collectPropagationTargets,
  LABEL_POSITIONS,
  LABEL_WRAPS,
  LABEL_BREAKS,
  LABEL_ALIGN_Y_VALUES,
  LABEL_ALIGNMENTS,
  FIELD_HEIGHTS,
  FIELD_SPACING_VALUES,
  ALIGN_ITEMS_VALUES,
  SIZE_VALUES,
  MAX_COLS
} from './ids-form-layout-common';

import type { IdsLabelStateMode } from '../../mixins/ids-label-state-mixin/ids-label-state-common';
import { isLabelStateValid } from '../../mixins/ids-label-state-mixin/ids-label-state-common';

const Base = IdsLocaleMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS Form Layout Component
 * @type {IdsFormLayout}
 * @inherits IdsElement
 * @mixes IdsEventsMixin
 * @mixes IdsLocaleMixin
 * @part layout - the form layout container element
 */
@customElement('ids-form-layout')
@scss(styles)
export default class IdsFormLayout extends Base {
  constructor() {
    super();
  }

  /** Tracks which attributes this layout has propagated onto each child */
  #propagatedKeys: PropagatedKeys = new WeakMap();

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array<string>} The attributes in an array
   */
  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      ...FORM_LAYOUT_ATTRIBUTES
    ];
  }

  /**
   * Invoked when the component is connected to the DOM
   */
  connectedCallback(): void {
    super.connectedCallback();
    this.#attachSlotChangeListener();
  }

  /**
   * Handle attribute changes
   * @param {string} name - Attribute name
   * @param {string} oldValue - Old value
   * @param {string} newValue - New value
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    super.attributeChangedCallback?.(name, oldValue, newValue);

    // Propagate settings when they change
    if ([
      attributes.LABEL_POSITION,
      attributes.LABEL_WIDTH,
      attributes.LABEL_WRAP,
      attributes.LABEL_BREAK,
      attributes.LABEL_ALIGN_Y,
      attributes.LABEL_ALIGNMENT,
      attributes.LABEL_STATE,
      attributes.COMPACT,
      attributes.FIELD_HEIGHT,
      attributes.FIELD_SPACING,
      attributes.NO_MARGINS,
      attributes.SIZE,
      attributes.GAP,
      attributes.ENABLE_CONTAINER,
      attributes.VALIDATE
    ].includes(name) && oldValue !== newValue) {
      this.#propagateSettingsToChildren();
    }
  }

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template(): string {
    return `<slot part="layout"></slot>`;
  }

  /**
   * Attach slotchange listener to propagate settings when children are slotted
   */
  #attachSlotChangeListener(): void {
    const slot = this.shadowRoot?.querySelector('slot');
    if (slot) {
      this.onEvent('slotchange', slot, () => {
        this.#propagateSettingsToChildren();
      });
    }
  }

  /**
   * Propagate label settings to all child form controls
   */
  #propagateSettingsToChildren(): void {
    // Collect direct propagation targets without descending into nested
    // form-layout containers (group / inline), which manage their own children.
    const children = collectPropagationTargets(this);

    children.forEach((child) => {
      const tagName = child.tagName.toLowerCase();
      // Wait for the custom element to be defined before propagating
      if (tagName.startsWith('ids-')) {
        customElements.whenDefined(tagName).then(() => {
          this.#propagateToChild(child as HTMLElement);
        }).catch(() => {
          // Element may not be registered, silently ignore
        });
      }
    });
  }

  /**
   * Propagate settings to a single child element
   * @param {HTMLElement} child - The child element
   */
  #propagateToChild(child: HTMLElement): void {
    const tracker = this.#propagatedKeys;
    const sync = (attr: string, prop: string, value: unknown, isBoolean = false) => {
      syncSettingToChild(child, attr, prop, value, tracker, isBoolean);
    };

    // Label position / label-related settings
    sync(attributes.LABEL_POSITION, 'labelPosition', this.labelPosition);
    sync(attributes.LABEL_WIDTH, 'labelWidth', this.labelWidth);
    sync(attributes.LABEL_WRAP, 'labelWrap', this.labelWrap);
    sync(attributes.LABEL_BREAK, 'labelBreak', this.labelBreak);
    sync(attributes.LABEL_ALIGN_Y, 'labelAlignY', this.labelAlignY);
    sync(attributes.LABEL_ALIGNMENT, 'labelAlignment', this.labelAlignment);
    sync(attributes.LABEL_STATE, 'labelState', this.labelState);

    // Compact and field height (also applies to inline wrappers, which forward
    // these to their own children)
    sync(attributes.COMPACT, 'compact', this.compact, true);
    sync(attributes.FIELD_HEIGHT, 'fieldHeight', this.fieldHeight);

    // Field spacing (Milestone 4)
    sync(attributes.NO_MARGINS, 'noMargins', this.noMargins, true);
    sync(attributes.MARGIN_BLOCK_END, 'marginBlockEnd', this.fieldSpacing);

    // Size — the form-layout owns field width via its grid columns, so default
    // children to `full` (fluid: fills and shrinks with the track) when no size
    // is set on the layout. Without this, controls keep their standalone fixed
    // width (e.g. `md` = 300px), which overflows tracks narrower than that at
    // small viewports. A `size` on the layout or an individual child still wins.
    sync(attributes.SIZE, 'size', this.size ?? 'full');

    // Validation — skip readonly/disabled controls (they can't be edited so a
    // required rule is meaningless). Also covers ids-form-layout-inline (which
    // exposes a shared `required` marker rather than a validate rule).
    const childEditable = !child.hasAttribute(attributes.READONLY) && !child.hasAttribute(attributes.DISABLED);
    sync(attributes.VALIDATE, 'validate', childEditable ? this.validate : null);
    if (child.tagName.toLowerCase() === 'ids-form-layout-inline') {
      // Inline wrappers surface a shared required marker rather than a validate rule.
      const inlineRequired = typeof this.validate === 'string' && this.validate.split(' ').includes('required');
      sync(attributes.REQUIRED, 'required', inlineRequired, true);
    }

    // Gap and enable-container to group / inline children
    const tagName = child.tagName.toLowerCase();
    if (tagName === 'ids-form-layout-group' || tagName === 'ids-form-layout-inline') {
      sync(attributes.GAP, 'gap', this.gap);
      sync(attributes.ENABLE_CONTAINER, 'enableContainer', this.enableContainer, true);

      // Nested containers manage their own descendants — tell them to re-read
      // inherited settings (validate, compact, field-height, etc.) from us.
      (child as any).propagateSettings?.();
    }
  }

  /**
   * Validate if a value is a valid column count
   * @param {string | number | null} value - The value to validate
   * @returns {boolean} True if valid
   */
  #isValidCols(value: string | number | null): boolean {
    if (value === null || value === undefined || value === '') return false;
    const num = typeof value === 'string' ? parseInt(value, 10) : value;
    return !Number.isNaN(num) && num >= 1 && num <= MAX_COLS;
  }

  /**
   * Validate if a value is a valid gap type
   * @param {string | null} value - The value to validate
   * @returns {boolean} True if valid
   */
  #isValidGap(value: string | null): boolean {
    return GAP_TYPES.includes(value as IdsFormLayoutGapType);
  }

  // ================================================
  // Column Settings
  // ================================================

  /**
   * Set the number of columns at base breakpoint
   * @param {string | null} value - Number of columns (1-12)
   */
  set cols(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COLS, String(value));
    } else {
      this.removeAttribute(attributes.COLS);
    }
  }

  /**
   * Get the number of columns at base breakpoint
   * @returns {string | null} The number of columns
   */
  get cols(): string | null {
    return this.getAttribute(attributes.COLS);
  }

  /**
   * Set the number of columns at XS breakpoint (320-575px)
   * @param {string | null} value - Number of columns (1-12)
   */
  set colsXs(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COLS_XS, String(value));
    } else {
      this.removeAttribute(attributes.COLS_XS);
    }
  }

  /**
   * Get the number of columns at XS breakpoint
   * @returns {string | null} The number of columns
   */
  get colsXs(): string | null {
    return this.getAttribute(attributes.COLS_XS);
  }

  /**
   * Set the number of columns at SM breakpoint (576-767px)
   * @param {string | null} value - Number of columns (1-12)
   */
  set colsSm(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COLS_SM, String(value));
    } else {
      this.removeAttribute(attributes.COLS_SM);
    }
  }

  /**
   * Get the number of columns at SM breakpoint
   * @returns {string | null} The number of columns
   */
  get colsSm(): string | null {
    return this.getAttribute(attributes.COLS_SM);
  }

  /**
   * Set the number of columns at MD breakpoint (768-927px)
   * @param {string | null} value - Number of columns (1-12)
   */
  set colsMd(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COLS_MD, String(value));
    } else {
      this.removeAttribute(attributes.COLS_MD);
    }
  }

  /**
   * Get the number of columns at MD breakpoint
   * @returns {string | null} The number of columns
   */
  get colsMd(): string | null {
    return this.getAttribute(attributes.COLS_MD);
  }

  /**
   * Set the number of columns at LG breakpoint (928-1183px)
   * @param {string | null} value - Number of columns (1-12)
   */
  set colsLg(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COLS_LG, String(value));
    } else {
      this.removeAttribute(attributes.COLS_LG);
    }
  }

  /**
   * Get the number of columns at LG breakpoint
   * @returns {string | null} The number of columns
   */
  get colsLg(): string | null {
    return this.getAttribute(attributes.COLS_LG);
  }

  /**
   * Set the number of columns at XL breakpoint (1184-2463px)
   * @param {string | null} value - Number of columns (1-12)
   */
  set colsXl(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COLS_XL, String(value));
    } else {
      this.removeAttribute(attributes.COLS_XL);
    }
  }

  /**
   * Get the number of columns at XL breakpoint
   * @returns {string | null} The number of columns
   */
  get colsXl(): string | null {
    return this.getAttribute(attributes.COLS_XL);
  }

  /**
   * Set the number of columns at XXL breakpoint (2464px+)
   * @param {string | null} value - Number of columns (1-12)
   */
  set colsXxl(value: string | null) {
    if (this.#isValidCols(value)) {
      this.setAttribute(attributes.COLS_XXL, String(value));
    } else {
      this.removeAttribute(attributes.COLS_XXL);
    }
  }

  /**
   * Get the number of columns at XXL breakpoint
   * @returns {string | null} The number of columns
   */
  get colsXxl(): string | null {
    return this.getAttribute(attributes.COLS_XXL);
  }

  // ================================================
  // Gap Settings
  // ================================================

  /**
   * Set the gap between fields
   * @param {IdsFormLayoutGapType} value - Gap size (none, sm, md, lg, xl)
   */
  set gap(value: IdsFormLayoutGapType) {
    if (this.#isValidGap(value as string)) {
      this.setAttribute(attributes.GAP, String(value));
    } else {
      this.removeAttribute(attributes.GAP);
    }
  }

  /**
   * Get the gap between fields
   * @returns {IdsFormLayoutGapType} The gap size
   */
  get gap(): IdsFormLayoutGapType {
    return this.getAttribute(attributes.GAP) as IdsFormLayoutGapType;
  }

  /**
   * Set the row gap (vertical gap between rows)
   * @param {IdsFormLayoutGapType} value - Gap size (none, sm, md, lg, xl)
   */
  set rowGap(value: IdsFormLayoutGapType) {
    if (this.#isValidGap(value as string)) {
      this.setAttribute(attributes.ROW_GAP, String(value));
    } else {
      this.removeAttribute(attributes.ROW_GAP);
    }
  }

  /**
   * Get the row gap
   * @returns {IdsFormLayoutGapType} The row gap size
   */
  get rowGap(): IdsFormLayoutGapType {
    return this.getAttribute(attributes.ROW_GAP) as IdsFormLayoutGapType;
  }

  /**
   * Set the column gap (horizontal gap between columns)
   * @param {IdsFormLayoutGapType} value - Gap size (none, sm, md, lg, xl)
   */
  set colGap(value: IdsFormLayoutGapType) {
    if (this.#isValidGap(value as string)) {
      this.setAttribute(attributes.COL_GAP, String(value));
    } else {
      this.removeAttribute(attributes.COL_GAP);
    }
  }

  /**
   * Get the column gap
   * @returns {IdsFormLayoutGapType} The column gap size
   */
  get colGap(): IdsFormLayoutGapType {
    return this.getAttribute(attributes.COL_GAP) as IdsFormLayoutGapType;
  }

  // ================================================
  // Label Position Settings
  // ================================================

  /**
   * Set the label position for all child form controls
   * @param {IdsFormLayoutLabelPosition} value - Label position (top, inline-start, inline-end)
   */
  set labelPosition(value: IdsFormLayoutLabelPosition) {
    if (LABEL_POSITIONS.includes(value)) {
      if (value) {
        this.setAttribute(attributes.LABEL_POSITION, value);
      } else {
        this.removeAttribute(attributes.LABEL_POSITION);
      }
    } else {
      this.removeAttribute(attributes.LABEL_POSITION);
    }
  }

  /**
   * Get the label position setting
   * @returns {IdsFormLayoutLabelPosition} The label position
   */
  get labelPosition(): IdsFormLayoutLabelPosition {
    return this.getAttribute(attributes.LABEL_POSITION) as IdsFormLayoutLabelPosition;
  }

  /**
   * Set the label width for all child form controls
   * @param {string | null} value - CSS width value (e.g., '120px', '30%')
   */
  set labelWidth(value: string | null) {
    if (value) {
      this.setAttribute(attributes.LABEL_WIDTH, value);
    } else {
      this.removeAttribute(attributes.LABEL_WIDTH);
    }
  }

  /**
   * Get the label width setting
   * @returns {string | null} The label width
   */
  get labelWidth(): string | null {
    return this.getAttribute(attributes.LABEL_WIDTH);
  }

  /**
   * Set the label wrap behavior for all child form controls
   * @param {IdsFormLayoutLabelWrap} value - Label wrap (ellipsis, wrap, ellipsis-no-stretch, wrap-no-stretch)
   */
  set labelWrap(value: IdsFormLayoutLabelWrap) {
    if (LABEL_WRAPS.includes(value)) {
      if (value) {
        this.setAttribute(attributes.LABEL_WRAP, value);
      } else {
        this.removeAttribute(attributes.LABEL_WRAP);
      }
    } else {
      this.removeAttribute(attributes.LABEL_WRAP);
    }
  }

  /**
   * Get the label wrap setting
   * @returns {IdsFormLayoutLabelWrap} The label wrap behavior
   */
  get labelWrap(): IdsFormLayoutLabelWrap {
    return this.getAttribute(attributes.LABEL_WRAP) as IdsFormLayoutLabelWrap;
  }

  /**
   * Set the label break behavior for all child form controls
   * @param {IdsFormLayoutLabelBreak} value - Label break (shrink-value, shrink-label, break)
   */
  set labelBreak(value: IdsFormLayoutLabelBreak) {
    if (LABEL_BREAKS.includes(value)) {
      if (value) {
        this.setAttribute(attributes.LABEL_BREAK, value);
      } else {
        this.removeAttribute(attributes.LABEL_BREAK);
      }
    } else {
      this.removeAttribute(attributes.LABEL_BREAK);
    }
  }

  /**
   * Get the label break setting
   * @returns {IdsFormLayoutLabelBreak} The label break behavior
   */
  get labelBreak(): IdsFormLayoutLabelBreak {
    return this.getAttribute(attributes.LABEL_BREAK) as IdsFormLayoutLabelBreak;
  }

  /**
   * Set the vertical alignment of labels for all child form controls
   * @param {IdsFormLayoutLabelAlignY} value - Vertical alignment (baseline, center, flex-start, flex-end, stretch)
   */
  set labelAlignY(value: IdsFormLayoutLabelAlignY) {
    if (LABEL_ALIGN_Y_VALUES.includes(value)) {
      if (value) {
        this.setAttribute(attributes.LABEL_ALIGN_Y, value);
      } else {
        this.removeAttribute(attributes.LABEL_ALIGN_Y);
      }
    } else {
      this.removeAttribute(attributes.LABEL_ALIGN_Y);
    }
  }

  /**
   * Get the vertical alignment of labels
   * @returns {IdsFormLayoutLabelAlignY} The vertical alignment
   */
  get labelAlignY(): IdsFormLayoutLabelAlignY {
    return this.getAttribute(attributes.LABEL_ALIGN_Y) as IdsFormLayoutLabelAlignY;
  }

  /**
   * Set the text alignment of labels for all child form controls
   * @param {IdsFormLayoutLabelAlignment} value - Text alignment (start, end)
   */
  set labelAlignment(value: IdsFormLayoutLabelAlignment) {
    if (LABEL_ALIGNMENTS.includes(value)) {
      if (value) {
        this.setAttribute(attributes.LABEL_ALIGNMENT, value);
      } else {
        this.removeAttribute(attributes.LABEL_ALIGNMENT);
      }
    } else {
      this.removeAttribute(attributes.LABEL_ALIGNMENT);
    }
  }

  /**
   * Get the text alignment of labels
   * @returns {IdsFormLayoutLabelAlignment} The text alignment
   */
  get labelAlignment(): IdsFormLayoutLabelAlignment {
    return this.getAttribute(attributes.LABEL_ALIGNMENT) as IdsFormLayoutLabelAlignment;
  }

  /**
   * Set the label state for all child form controls
   * @param {IdsLabelStateMode} value - Label state (hidden, collapsed)
   */
  set labelState(value: IdsLabelStateMode) {
    if (isLabelStateValid(value)) {
      if (value) {
        this.setAttribute(attributes.LABEL_STATE, value);
      } else {
        this.removeAttribute(attributes.LABEL_STATE);
      }
    } else {
      this.removeAttribute(attributes.LABEL_STATE);
    }
  }

  /**
   * Get the label state setting
   * @returns {IdsLabelStateMode} The label state
   */
  get labelState(): IdsLabelStateMode {
    return this.getAttribute(attributes.LABEL_STATE) as IdsLabelStateMode;
  }

  // ================================================
  // Compact Mode and Field Height Settings (Milestone 3)
  // ================================================

  /**
   * Set compact mode for all child form controls
   * @param {boolean | string} value - Whether to enable compact mode
   */
  set compact(value: boolean | string) {
    const isCompact = stringToBool(value);
    if (isCompact) {
      this.setAttribute(attributes.COMPACT, '');
    } else {
      this.removeAttribute(attributes.COMPACT);
    }
  }

  /**
   * Get the compact mode setting
   * @returns {boolean} Whether compact mode is enabled
   */
  get compact(): boolean {
    return stringToBool(this.getAttribute(attributes.COMPACT));
  }

  /**
   * Set the field height for all child form controls
   * @param {IdsFormLayoutFieldHeight} value - Field height (xxs, xs, sm, md, lg)
   */
  set fieldHeight(value: IdsFormLayoutFieldHeight) {
    if (FIELD_HEIGHTS.includes(value)) {
      if (value) {
        this.setAttribute(attributes.FIELD_HEIGHT, value);
      } else {
        this.removeAttribute(attributes.FIELD_HEIGHT);
      }
    } else {
      this.removeAttribute(attributes.FIELD_HEIGHT);
    }
  }

  /**
   * Get the field height setting
   * @returns {IdsFormLayoutFieldHeight} The field height
   */
  get fieldHeight(): IdsFormLayoutFieldHeight {
    return this.getAttribute(attributes.FIELD_HEIGHT) as IdsFormLayoutFieldHeight;
  }

  // ================================================
  // Field Spacing Settings (Milestone 4)
  // ================================================

  /**
   * Set the field spacing (margin-block-end) for all child form controls
   * @param {IdsFormLayoutFieldSpacing} value - Spacing token key (none, 4xs, 3xs, 2xs, xs, sm, md, lg, xl, 2xl, 3xl, 4xl)
   */
  set fieldSpacing(value: IdsFormLayoutFieldSpacing) {
    if (FIELD_SPACING_VALUES.includes(value)) {
      if (value) {
        this.setAttribute(attributes.FIELD_SPACING, value);
      } else {
        this.removeAttribute(attributes.FIELD_SPACING);
      }
    } else {
      this.removeAttribute(attributes.FIELD_SPACING);
    }
  }

  /**
   * Get the field spacing setting
   * @returns {IdsFormLayoutFieldSpacing} The field spacing token key
   */
  get fieldSpacing(): IdsFormLayoutFieldSpacing {
    return this.getAttribute(attributes.FIELD_SPACING) as IdsFormLayoutFieldSpacing;
  }

  /**
   * Set no-margins mode for all child form controls
   * @param {boolean | string} value - Whether to remove margins from children
   */
  set noMargins(value: boolean | string) {
    const noMargins = stringToBool(value);
    if (noMargins) {
      this.setAttribute(attributes.NO_MARGINS, '');
    } else {
      this.removeAttribute(attributes.NO_MARGINS);
    }
  }

  /**
   * Get the no-margins setting
   * @returns {boolean} Whether no-margins mode is enabled
   */
  get noMargins(): boolean {
    return stringToBool(this.getAttribute(attributes.NO_MARGINS));
  }

  // ================================================
  // Container Query Settings (Milestone 7)
  // ================================================

  /**
   * Set whether to use container queries instead of media queries
   * When enabled, responsive column breakpoints respond to the container size
   * rather than viewport size.
   * @param {boolean | string} value - Whether to enable container queries
   */
  set enableContainer(value: boolean | string) {
    const enabled = stringToBool(value);
    if (enabled) {
      this.setAttribute(attributes.ENABLE_CONTAINER, '');
    } else {
      this.removeAttribute(attributes.ENABLE_CONTAINER);
    }
  }

  /**
   * Get the enable-container setting
   * @returns {boolean} Whether container queries are enabled
   */
  get enableContainer(): boolean {
    return stringToBool(this.getAttribute(attributes.ENABLE_CONTAINER));
  }

  // ================================================
  // Align Items Settings
  // ================================================

  /**
   * Set the vertical alignment of grid items
   * @param {IdsFormLayoutAlignItems} value - Alignment value (anchor-center, center, baseline, end)
   */
  set alignItems(value: IdsFormLayoutAlignItems) {
    if (ALIGN_ITEMS_VALUES.includes(value)) {
      if (value) {
        this.setAttribute(attributes.ALIGN_ITEMS, value);
      } else {
        this.removeAttribute(attributes.ALIGN_ITEMS);
      }
    } else {
      this.removeAttribute(attributes.ALIGN_ITEMS);
    }
  }

  /**
   * Get the align-items setting
   * @returns {IdsFormLayoutAlignItems} The alignment value
   */
  get alignItems(): IdsFormLayoutAlignItems {
    return this.getAttribute(attributes.ALIGN_ITEMS) as IdsFormLayoutAlignItems;
  }

  // ================================================
  // Size Settings (propagates to children)
  // ================================================

  /**
   * Set the size (width) for all child form controls
   * @param {IdsFormLayoutSize} value - Size value (xs, sm, mm, md, lg, full)
   */
  set size(value: IdsFormLayoutSize) {
    if (SIZE_VALUES.includes(value)) {
      if (value) {
        this.setAttribute(attributes.SIZE, value);
      } else {
        this.removeAttribute(attributes.SIZE);
      }
    } else {
      this.removeAttribute(attributes.SIZE);
    }
  }

  /**
   * Get the size setting
   * @returns {IdsFormLayoutSize} The size value
   */
  get size(): IdsFormLayoutSize {
    return this.getAttribute(attributes.SIZE) as IdsFormLayoutSize;
  }

  // ================================================
  // Validation Settings (propagates to children)
  // ================================================

  /**
   * Set validation rule(s) for all child form controls
   * @param {string | null} value - Validation rule(s) (e.g., 'required', 'email', 'required email')
   */
  set validate(value: string | null) {
    if (value) {
      this.setAttribute(attributes.VALIDATE, value);
    } else {
      this.removeAttribute(attributes.VALIDATE);
    }
  }

  /**
   * Get the validation setting
   * @returns {string | null} The validation rule(s)
   */
  get validate(): string | null {
    return this.getAttribute(attributes.VALIDATE);
  }
}
